import React, { useEffect } from 'react';
import { Question } from '../../types';
import { v4 as uuidv4 } from 'uuid';
import './QuestionOptionsEditor.css';

import { FaRegTrashAlt } from 'react-icons/fa';
import { IoMdAdd } from 'react-icons/io';

interface QuestionOptionsEditorProps {
  question: Question;
  updateOption: <K extends keyof Question>(key: K, value: Question[K]) => void;
  dir?: 'rtl' | 'ltr';
  locked?: boolean;
  labelSuffix?: string;
}

const QuestionOptionsEditor: React.FC<QuestionOptionsEditorProps> = ({
  question,
  updateOption,
  dir = 'rtl',
  locked = false,
  labelSuffix = '',
}) => {
  useEffect(() => {
    if (locked) {
      return;
    }
    if (!question.options || question.options.length === 0) {
      const emptyOptions = [
        { guid: uuidv4(), text: '', exclusive: false, active: true },
        { guid: uuidv4(), text: '', exclusive: false, active: true },
      ];
      updateOption('options', emptyOptions);
    }
  }, [locked, question.options, updateOption]);

  const handleUpdateOption = (index: number, value: string) => {
    const updated = [...question.options];
    updated[index].text = value;
    updateOption('options', updated);
  };

  const deleteOption = (index: number) => {
    if (question.options.length > 2) {
      const deletedGuid = question.options[index].guid;
      const updated = [...question.options];
      updated.splice(index, 1);
      updateOption('options', updated);
      // Keep template option_guids in sync so computeChildChanges emits `remove` for the affected slot
      if (question.child_question_templates?.length) {
        const updatedTemplates = question.child_question_templates.map((t) => ({
          ...t,
          option_guids: (t.option_guids ?? []).filter((g) => g !== deletedGuid),
        }));
        updateOption('child_question_templates', updatedTemplates);
      }
    }
  };

  const addOptionAfter = (index: number) => {
    const updated = [...question.options];
    updated.splice(index + 1, 0, {
      guid: uuidv4(),
      text: '',
      exclusive: false,
      active: true,
    });
    updateOption('options', updated);
  };

  const handleCheckboxChange = (index: number, field: 'exclusive' | 'active', value: boolean) => {
    const updated = [...question.options];
    updated[index][field] = value;
    updateOption('options', updated);
  };

  return (
    <table className='option-table'>
      <thead>
        <tr>
          {/* delete icon */}
          {question.options.length > 2 && <th></th>}
          {/* plus icon */}
          <th></th>
          <th style={{ textAlign: 'right' }}>אפשרות{labelSuffix}</th>
          <th style={{ textAlign: 'center' }}>בלעדי</th>
          <th style={{ textAlign: 'center' }}>פעיל</th>
        </tr>
      </thead>
      <tbody>
        {question.options.map((option, idx) => (
          <tr key={option.guid}>
            {question.options.length > 2 && (
              <td>
                <button
                  className='question-option-action-button'
                  onClick={() => deleteOption(idx)}
                  disabled={locked}
                >
                  <FaRegTrashAlt />
                </button>
              </td>
            )}
            <td>
              <button
                className='question-option-action-button'
                onClick={() => addOptionAfter(idx)}
                disabled={locked}
              >
                <IoMdAdd />
              </button>
            </td>
            <td>
              <input
                className='input-field'
                style={{ direction: dir }}
                value={option.text}
                onChange={(e) => handleUpdateOption(idx, e.target.value)}
              />
            </td>
            <td style={{ textAlign: 'center' }}>
              <input
                type='checkbox'
                checked={option.exclusive}
                onChange={(e) => handleCheckboxChange(idx, 'exclusive', e.target.checked)}
                disabled={locked}
              />
            </td>
            <td style={{ textAlign: 'center' }}>
              <input
                type='checkbox'
                checked={option.active}
                onChange={(e) => handleCheckboxChange(idx, 'active', e.target.checked)}
                disabled={locked}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default QuestionOptionsEditor;
